import { ServerError } from "./../define/ServerError";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import ImageLayer from "ol/layer/Image";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { MessageDefine } from "@/components/pages/common/MessageDefine";
import { CommonView } from "./../../components/pages/common/CommonView";
import { Pointer } from "ol/interaction";
import { GeoMap } from "../GeoMap";
import { Feature, MapBrowserEvent } from "ol";
import { Layer } from "ol/layer";
import { Stroke, Style } from "ol/style";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import ImageWMSEx from "../net/ImageWMSEx";
import { Pixel } from "ol/pixel";

/**
 * マウスダウンイベント定義値
 */
const EVENT_MOUSE_TYPE_DOWN = "pointerdown";

/**
 * マウスアップイベント定義値
 */
const EVENT_MOUSE_TYPE_UP = "pointerup";

/**
 * マウス左クリックボタン定義値
 */
const EVENT_MOUSE_LEFT_BUTTON = 0;

/**
 * マウスダウン地点ピクセル
 */
let downPointPixel: Pixel;

/**
 * 地物選択・ドラッグ閾値
 */
const SELECT_DRAG_THRESHOLD = 2;

/**
 * WMSサーバーURL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * レイヤ物理キー
 */
const LAYER_KEY = "t_municipalities_poly";

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 9;

/**
 * 市区町村選択クラス
 */
export class ExportAreaSelectInteraction {
  /**
   * 地図クラス
   */
  _geoMap: GeoMap;

  /**
   * 市区町村ソース
   */
  _wmsImageSource: ImageWMSEx;

  /**
   * 市区町村レイヤ
   */
  _imageLayer: ImageLayer<ImageWMSEx>;

  /**
   * 市区町村ソース
   */
  _selecetedVectorSource: VectorSource;

  /**
   * 選択中市区町村レイヤ
   */
  _selectedVectorLayer: VectorLayer<VectorSource>;

  /**
   * 選択中市区町村ポリゴン情報
   */
  private _selectedExportAreaFeature: Feature = new Feature();

  /**
   * 市区町村選択コールバック処理
   */
  _callbackSelectedExportArea: (address: string) => void;

  /**
   * （エラー表示用）画面クラス
   */
  _commonView: CommonView;

  /**
   * マウスイベント操作用Interaction
   */
  private _pointer: Pointer;

  /**
   * コンストラクタ
   */
  constructor(map: GeoMap, commonView: CommonView) {
    this._geoMap = map;
    this._commonView = commonView;
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: LAYER_KEY,
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._imageLayer = new ImageLayer({
      source: this._wmsImageSource,
      visible: false,
    });
    this._selecetedVectorSource = new VectorSource();
    this._selectedVectorLayer = new VectorLayer({
      minZoom: LAYER_MIN_ZOOMLEVEL,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      source: this._selecetedVectorSource,
    });
  }

  /**
   * 選択中市区町村ポリゴン情報 Getter
   */
  get selectedExportAreaFeature() {
    return this._selectedExportAreaFeature;
  }

  /**
   * 市区町村選択開始
   */
  start(callbackSelectedExportArea: (address: string) => void) {
    this._callbackSelectedExportArea = callbackSelectedExportArea;
    this._imageLayer.setVisible(true);
    this._selectedVectorLayer.setVisible(true);
    this._pointer = new Pointer({
      handleEvent: this.drawHandleEvent.bind(this),
    });
    this._geoMap._map.addInteraction(this._pointer);
  }

  /**
   * 市区町村選択終了
   */
  end() {
    this._geoMap._map.removeInteraction(this._pointer);
    this._imageLayer.setVisible(false);
    this._selectedVectorLayer.setVisible(false);
  }

  /**
   * 選択状態をクリア
   */
  clear() {
    this._selectedExportAreaFeature = new Feature();
    this._selecetedVectorSource.clear();
  }

  /*
   * Pointerから呼び出されるマウスイベント処理
   * @param MapBrowserEvent OpenLayersマップイベント
   */
  private drawHandleEvent(evt: MapBrowserEvent<any>): boolean {
    if (evt.originalEvent.button == EVENT_MOUSE_LEFT_BUTTON) {
      if (evt.type == EVENT_MOUSE_TYPE_DOWN) {
        downPointPixel = evt.pixel;
      } else if (evt.type == EVENT_MOUSE_TYPE_UP) {
        if (
          downPointPixel &&
          (downPointPixel[0] - SELECT_DRAG_THRESHOLD > evt.pixel[0] ||
            downPointPixel[0] + SELECT_DRAG_THRESHOLD < evt.pixel[0] ||
            downPointPixel[1] - SELECT_DRAG_THRESHOLD > evt.pixel[1] ||
            downPointPixel[1] + SELECT_DRAG_THRESHOLD < evt.pixel[1])
        ) {
          return true;
        }

        const cqlParameter = new CqlParameter(LAYER_KEY, null);
        const mapSize = evt.map.getSize();
        const WmsGetter = new WMSGetFeatureManager();
        WmsGetter.getInfo(
          URL_API,
          evt.map.getView().calculateExtent(mapSize).join(","),
          [cqlParameter],
          Math.floor(evt.pixel[0]),
          Math.floor(evt.pixel[1]),
          mapSize[1],
          mapSize[0]
        )
          .then((res) => {
            const selectedFeature = new GeoJSON().readFeatures(res);
            if (selectedFeature) {
              if (selectedFeature.length === 0) return true;
              this._selectedExportAreaFeature = selectedFeature[0];
              const style = new Style({
                stroke: new Stroke({
                  color: "hotpink",
                  width: 2,
                }),
              });
              this._selectedExportAreaFeature.setStyle(style);
              this._selecetedVectorSource.clear();
              this._selecetedVectorSource.addFeature(
                this._selectedExportAreaFeature
              );
              const prefecture =
                this._selectedExportAreaFeature.get("都道府県名");
              const city = this._selectedExportAreaFeature.get("市区町村名");
              const address = prefecture + city;
              this._callbackSelectedExportArea(address);
            } else {
              console.error("Area Not Found");
            }
          })
          .catch((err) => {
            if (err instanceof ServerError) {
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.E0001.getMessage([err._url, err._status])
              );
            } else {
              console.error(err);
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.E0002.getMessage(["市区町村取得時エラー"])
              );
            }
            return true;
          });
      }
    }
    return true;
  }

  /**
   * 市区町村レイヤを取得
   */
  getLayer(): Layer[] {
    return [this._imageLayer, this._selectedVectorLayer];
  }
}
